// Tasks of a whole account live in one Firestore document. Without an owner every
// colleague saw every task, so each task carries the uid of the person it belongs to
// and a visibility that the owner controls in the team management.
export type TaskSharingMode = 'private' | 'shared' | 'delegate';

export const TASK_SHARING_MODES: TaskSharingMode[] = ['private', 'shared', 'delegate'];

export const DEFAULT_TASK_SHARING_MODE: TaskSharingMode = 'private';

export interface TaskOwnership {
  ownerUid: string;
  ownerEmail: string;
  shared: boolean;
  delegatable: boolean;
}

export interface OwnedTask {
  ownerUid?: string;
  ownerEmail?: string;
  shared?: boolean;
  delegatable?: boolean;
  assignedToUid?: string;
  [key: string]: any;
}

export function normalizeSharingMode(mode: any): TaskSharingMode {
  return TASK_SHARING_MODES.includes(mode) ? mode : DEFAULT_TASK_SHARING_MODE;
}

export function taskOwnership(uid: string, email: string, mode: any): TaskOwnership {
  const sharing = normalizeSharingMode(mode);
  return {
    ownerUid: String(uid || ''),
    ownerEmail: String(email || '').trim().toLowerCase(),
    shared: sharing !== 'private',
    delegatable: sharing === 'delegate'
  };
}

export function canSeeTask(task: OwnedTask | null | undefined, uid: string): boolean {
  if (!task || !uid) return false;
  if (!task.ownerUid) return false;
  if (task.ownerUid === uid) return true;
  if (task.assignedToUid === uid) return true;
  return task.shared === true;
}

// A task the current user may see but not own stays read-only: it is written back
// untouched so saving a personal list never overwrites a colleague's task.
export function isTaskEditableBy(task: OwnedTask | null | undefined, uid: string): boolean {
  if (!task || !uid) return false;
  return task.ownerUid === uid || task.assignedToUid === uid;
}

export interface TaskSplit<T extends OwnedTask> {
  own: T[];
  foreign: T[];
  backfilled: boolean;
}

/**
 * Splits a stored task list into the entries the current user owns and the entries that
 * must be written back unchanged. Legacy tasks without an owner are adopted by the
 * account owner so no task stays ownerless and nobody else keeps seeing them.
 */
export function splitTasksByOwner<T extends OwnedTask>(
  tasks: T[] | null | undefined,
  uid: string,
  accountOwnerUid: string
): TaskSplit<T> {
  const own: T[] = [];
  const foreign: T[] = [];
  let backfilled = false;

  for (const task of Array.isArray(tasks) ? tasks : []) {
    if (!task) continue;
    let entry = task;
    if (!entry.ownerUid && accountOwnerUid) {
      entry = { ...entry, ownerUid: accountOwnerUid, shared: false };
      backfilled = true;
    }
    if (isTaskEditableBy(entry, uid)) own.push(entry);
    else foreign.push(entry);
  }

  return { own, foreign, backfilled };
}

// A task handed over to the current user keeps its original owner and visibility,
// only the owner's own entries follow the owner's sharing mode.
export function applyOwnership(task: OwnedTask, ownership: TaskOwnership): TaskOwnership {
  if (task?.ownerUid && task.ownerUid !== ownership.ownerUid) {
    return {
      ownerUid: task.ownerUid,
      ownerEmail: task.ownerEmail || '',
      shared: task.shared === true,
      delegatable: task.delegatable === true
    };
  }
  return ownership;
}

// True when stored entries still carry an outdated visibility, e.g. after the owner
// switched the sharing mode in the team management.
export function needsOwnershipRefresh(tasks: OwnedTask[] | null | undefined, ownership: TaskOwnership): boolean {
  return (Array.isArray(tasks) ? tasks : []).some(task =>
    !task?.ownerUid ||
    (task.ownerUid === ownership.ownerUid &&
      (task.shared !== ownership.shared || task.delegatable !== ownership.delegatable))
  );
}

// Tasks visible but not editable, e.g. a colleague who shares their list.
export function visibleForeignTasks<T extends OwnedTask>(foreign: T[], uid: string): T[] {
  return foreign.filter(task => canSeeTask(task, uid));
}
